import { useEffect, useMemo, useState } from 'react';
import { useMap } from 'react-map-gl/maplibre';

const ConnectionLayer = ({ shopGroups, validShops, validCabinets, hiddenGroups = new Set() }) => {
    const mapRef = useMap();
    const [mapReady, setMapReady] = useState(false);

    // Check if map is ready for operations
    useEffect(() => {
        if (!mapRef.current) return;

        const map = mapRef.current.getMap();

        const checkMapReady = () => {
            if (map && map.loaded() && map.isStyleLoaded()) {
                setMapReady(true);
            }
        };

        // Check immediately
        checkMapReady();

        // Listen for load events
        map.on('load', checkMapReady);
        map.on('styledata', checkMapReady);

        return () => {
            if (map.off) {
                map.off('load', checkMapReady);
                map.off('styledata', checkMapReady);
            }
        };
    }, [mapRef]);

    // Generate GeoJSON data for connections
    const connectionGeoJSON = useMemo(() => {
        const features = [];

        Object.entries(shopGroups).forEach(([gl, group]) => {
            if (hiddenGroups.has(gl)) {
                return; // Skip hidden groups
            }

            group.shops.forEach(shop => {
                if (!shop.lat || !shop.lng) {
                    return;
                }

                const shopCabinets = validCabinets.filter(cabinet =>
                    cabinet.shop_code === shop.code
                );

                if (shopCabinets.length === 0) return;

                // Create line features for each shop-cabinet connection
                shopCabinets.forEach((cabinet, index) => {
                    if (!cabinet.lat || !cabinet.lng) return;

                    const lineFeature = {
                        type: "Feature",
                        geometry: {
                            type: "LineString",
                            coordinates: [
                                [parseFloat(shop.lng), parseFloat(shop.lat)],
                                [parseFloat(cabinet.lng), parseFloat(cabinet.lat)]
                            ]
                        },
                        properties: {
                            shopCode: shop.code,
                            cabinetCode: cabinet.code,
                            groupGL: gl,
                            groupColor: group.color || '#ff0000', // Default to red for visibility
                            shopName: shop.name,
                            cabinetId: cabinet._id || `${cabinet.code}-${index}`
                        }
                    };

                    features.push(lineFeature);
                });
            });
        });

        return {
            type: "FeatureCollection",
            features
        };
    }, [shopGroups, validCabinets, hiddenGroups]);

    // Add/update native map layers when data changes
    useEffect(() => {
        if (!mapRef.current || !mapReady) return;

        const map = mapRef.current.getMap();
        if (!map || typeof map.addSource !== 'function') return;

        const sourceId = 'shop-connections';
        const layerId = 'connections-layer';

        try {
            // Remove existing layers and sources
            if (map.getLayer(layerId)) {
                map.removeLayer(layerId);
            }
            if (map.getSource(sourceId)) {
                map.removeSource(sourceId);
            }

            // Add new source and layer only if we have features
            if (connectionGeoJSON.features.length > 0) {
                // Add GeoJSON source
                map.addSource(sourceId, {
                    type: 'geojson',
                    data: connectionGeoJSON
                });

                // Add line layer with enhanced styling
                map.addLayer({
                    id: layerId,
                    type: 'line',
                    source: sourceId,
                    layout: {
                        'line-join': 'round',
                        'line-cap': 'round'
                    },
                    paint: {
                        // Dynamic color based on shop group (fallback to red for visibility)
                        'line-color': [
                            'case',
                            ['has', 'groupColor'],
                            ['get', 'groupColor'],
                            '#ff0000' // Bright red fallback
                        ],
                        // More visible line width
                        'line-width': [
                            'interpolate',
                            ['linear'],
                            ['zoom'],
                            8, 2,      // At zoom 8, width = 2
                            12, 4,     // At zoom 12, width = 4
                            16, 6      // At zoom 16, width = 6
                        ],
                        // Higher opacity for better visibility
                        'line-opacity': 0.9,
                        // Dashed line pattern
                        'line-dasharray': [3, 3]
                    }
                }); // Remove beforeId temporarily to ensure layer gets added
            }
        } catch (error) {
            console.warn('Error managing connection layers:', error);
        }

        // Cleanup function
        return () => {
            try {
                // Capture map instance for cleanup
                if (map && typeof map.getLayer === 'function') {
                    if (map.getLayer(layerId)) {
                        map.removeLayer(layerId);
                    }
                    if (map.getSource(sourceId)) {
                        map.removeSource(sourceId);
                    }
                }
            } catch (error) {
                console.warn('Error cleaning up connection layers:', error);
            }
        };
    }, [mapRef, mapReady, connectionGeoJSON]);

    // Add interactive features
    useEffect(() => {
        if (!mapRef.current || !mapReady) return;

        const map = mapRef.current.getMap();
        if (!map || typeof map.on !== 'function') return;

        const layerId = 'connections-layer';

        // Add hover effect
        const onMouseEnter = () => {
            map.getCanvas().style.cursor = 'pointer';
        };

        const onMouseLeave = () => {
            map.getCanvas().style.cursor = '';
        };

        // Add click handler for connection details
        const onClick = (e) => {
            if (e.features.length > 0) {
                const feature = e.features[0];
                const { shopCode, cabinetCode, shopName } = feature.properties;

                // You can extend this to show popup or highlight related markers
                console.log(`Connection: ${shopName} (${shopCode}) → Cabinet ${cabinetCode}`);
            }
        };

        try {
            // Check if layer exists before adding listeners
            if (map.getLayer(layerId)) {
                map.on('mouseenter', layerId, onMouseEnter);
                map.on('mouseleave', layerId, onMouseLeave);
                map.on('click', layerId, onClick);
            }
        } catch (error) {
            console.warn('Error adding connection layer interactions:', error);
        }

        return () => {
            try {
                // Use the same map instance for cleanup
                if (map && typeof map.off === 'function' && map.getLayer(layerId)) {
                    map.off('mouseenter', layerId, onMouseEnter);
                    map.off('mouseleave', layerId, onMouseLeave);
                    map.off('click', layerId, onClick);
                }
            } catch (error) {
                console.warn('Error removing connection layer interactions:', error);
            }
        };
    }, [mapRef, mapReady, connectionGeoJSON]);

    // This component doesn't render anything directly - it manages map layers
    return null;
};

export default ConnectionLayer;
